import { DateTime } from "luxon";

/** Which date of the range a click sets */
export enum RangeEndpoint {
  start = "start",
  end = "end",
}

/** The calendar unit a clicked cell spans */
export type CellUnit = "day" | "month";

/** A day or month cell of the calendar or list */
export type Cell = [date: DateTime, unit: CellUnit];

/**
 * A plain click selects the clicked cell alone. With Start or End chosen in the heading, the
 * click moves only that endpoint; {@link isPickable} keeps it from crossing the other one.
 *
 * @returns The new range
 * @param date The clicked cell
 * @param unit The unit the cell spans
 * @param endpoint The endpoint this click moves, if one was chosen
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function pickDate(
  date: DateTime,
  unit: CellUnit,
  endpoint?: RangeEndpoint,
  start?: Date,
  end?: Date,
): [start: Date, end: Date] {
  const from = date.startOf(unit).toJSDate();
  const to = date.endOf(unit).toJSDate();
  if (endpoint === RangeEndpoint.end && start) return [start, to];
  if (endpoint === RangeEndpoint.start && end) return [from, end];
  return [from, to];
}

/**
 * @returns False for a cell the edited endpoint cannot move to without crossing the other one
 * @param date The cell to test
 * @param unit The unit the cell spans
 * @param endpoint The endpoint being edited, if any
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function isPickable(
  date: DateTime,
  unit: CellUnit,
  endpoint?: RangeEndpoint,
  start?: Date,
  end?: Date,
): boolean {
  if (endpoint === RangeEndpoint.end && start) {
    return date.endOf(unit).toJSDate() >= start;
  }
  if (endpoint === RangeEndpoint.start && end) {
    return date.startOf(unit).toJSDate() <= end;
  }
  return true;
}

/**
 * @returns The range spanning the anchor cell and the clicked cell, in either direction
 * @param date The shift-clicked cell
 * @param unit The unit the cell spans
 * @param anchor The cell the range grew from, which stays selected
 */
export function extendRange(
  date: DateTime,
  unit: CellUnit,
  [anchorStart, anchorEnd]: [start: Date, end: Date],
): [start: Date, end: Date] {
  const from = date.startOf(unit).toJSDate();
  const to = date.endOf(unit).toJSDate();
  return from < anchorStart ? [from, anchorEnd] : [anchorStart, to];
}

/**
 * @returns The anchor while it lies inside the active range, else the range's first day, so
 * a range set elsewhere (e.g. from the project tree) can still be extended
 * @param anchor The cell last picked alone
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function resolveAnchor(
  anchor?: [start: Date, end: Date],
  start?: Date,
  end?: Date,
): [start: Date, end: Date] | undefined {
  if (!start) return;
  if (anchor && anchor[0] >= start && (!end || anchor[1] <= end)) {
    return anchor;
  }
  return pickDate(DateTime.fromJSDate(start), "day");
}
